import React from 'react';
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer, TableRow, TextField, 
  Paper, Typography, Checkbox, FormControlLabel, Select, MenuItem, FormControl, 
  InputLabel, Button, TableHead
} from '@mui/material';
import SimpleDatePicker from '../../../ui/simpleDatePicker';

const DIAGNOSIS_ROWS = [
  'No Rx Required',
  'Myope',
  'Hyperope',
  'Astigmatism',
  'Presbyope',
  'Bin Prob',
  'Amblyopia',
  'Suppression',
  'Strabismus',
  'Other'
];

const RX_COLUMNS = ['Sphere', 'Cylinder', 'Axis', 'Prism', 'Add'];

export function getDefaultDiagnosisValues() {
  const diagnosisInit = {};
  DIAGNOSIS_ROWS.forEach(row => {
    diagnosisInit[row] = { od: false, os: false };
  });
  
  const rxInit = {};
  RX_COLUMNS.forEach(col => {
    rxInit[col] = { od: '', os: '' };
  });

  return {
    diagnosis: diagnosisInit,
    comparedWithOldRx: false,
    warnedRxAdapt: false,
    date: '',
    expiryDate: '',
    rx: rxInit,
    assessmentAndPlan: '',
    diagnosticCode: '',
    referral: '',
    reason: '',
    tpas: '',
    recallValue: '',
    recallFrequency: '',
    recallDate: '',
  };
}

function DiagnosisTab({ value = {}, onChange }) {
  const v = { ...getDefaultDiagnosisValues(), ...value };

  const calculateRecallDate = (recallValue, frequency) => {
    if (!recallValue || !frequency) return '';
    
    const now = new Date();
    const numValue = parseInt(recallValue);
    if (isNaN(numValue)) return '';

    let futureDate = new Date(now);
    
    switch (frequency) {
      case 'day':
        futureDate.setDate(now.getDate() + numValue);
        break;
      case 'week':
        futureDate.setDate(now.getDate() + (numValue * 7));
        break;
      case 'month':
        futureDate.setMonth(now.getMonth() + numValue);
        break;
      case 'year':
        futureDate.setFullYear(now.getFullYear() + numValue);
        break;
      default:
        return '';
    }
    
    return futureDate.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const handleRecallChange = (field, newValue) => {
    const updatedValue = { ...v, [field]: newValue };
    
    // Recalculate recall date if both recall value and frequency are present
    if (field === 'recallValue' || field === 'recallFrequency') {
      const recallValue = field === 'recallValue' ? newValue : v.recallValue;
      const recallFrequency = field === 'recallFrequency' ? newValue : v.recallFrequency;
      updatedValue.recallDate = calculateRecallDate(recallValue, recallFrequency);
    }
    
    onChange?.(updatedValue);
  };

  const printTPAs = () => {
    if (!v.tpas) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>TPAs</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            pre { white-space: pre-wrap; word-wrap: break-word; }
          </style>
        </head>
        <body>
          <h2>TPAs</h2>
          <pre>${v.tpas}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Column 1: 30% */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Diagnosis</Typography>
            
            {/* Diagnosis Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Condition</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>OD</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>OS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DIAGNOSIS_ROWS.map((row) => (
                    <TableRow key={row}>
                      <TableCell>{row}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={v.diagnosis?.[row]?.od || false}
                          onChange={(e) => onChange?.({ 
                            ...v, 
                            diagnosis: { 
                              ...v.diagnosis, 
                              [row]: { ...v.diagnosis?.[row], od: e.target.checked } 
                            }
                          })}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={v.diagnosis?.[row]?.os || false}
                          onChange={(e) => onChange?.({ 
                            ...v, 
                            diagnosis: { 
                              ...v.diagnosis, 
                              [row]: { ...v.diagnosis?.[row], os: e.target.checked } 
                            }
                          })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Additional Checkboxes */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={v.comparedWithOldRx || false}
                    onChange={(e) => onChange?.({ ...v, comparedWithOldRx: e.target.checked })}
                  />
                }
                label="Compared w/old Rx"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={v.warnedRxAdapt || false}
                    onChange={(e) => onChange?.({ ...v, warnedRxAdapt: e.target.checked })}
                  />
                }
                label="Warned: Rx adapt."
              />
            </Box>
          </Paper>
        </Grid>

        {/* Column 2: 70% */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Prescription & Assessment</Typography>
            
            {/* Rx Date Field */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SimpleDatePicker
                    label="Rx Date"
                    value={v.date || ''}
                    onChange={(date) => onChange?.({ ...v, date: date || '' })}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SimpleDatePicker
                    label="Expiry Date"
                    value={v.expiryDate || ''}
                    onChange={(date) => onChange?.({ ...v, expiryDate: date || '' })}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Prescription Table */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Prescription</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Eye</TableCell>
                      {RX_COLUMNS.map((col) => (
                        <TableCell key={col} align="center" sx={{ fontWeight: 'bold' }}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['OD', 'OS'].map((eye) => (
                      <TableRow key={eye}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{eye}</TableCell>
                        {RX_COLUMNS.map((col) => (
                          <TableCell key={col} align="center">
                            <TextField
                              size="small"
                              value={v.rx?.[col]?.[eye.toLowerCase()] || ''}
                              onChange={(e) => onChange?.({ 
                                ...v, 
                                rx: { 
                                  ...v.rx, 
                                  [col]: { ...v.rx?.[col], [eye.toLowerCase()]: e.target.value } 
                                }
                              })}
                              variant="outlined"
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Assessment and Plan */}
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Assessment and Plan:"
                value={v.assessmentAndPlan || ''}
                onChange={(e) => onChange?.({ ...v, assessmentAndPlan: e.target.value })}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
              />
            </Box>

            {/* Diagnostic Code */}
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Diagnostic Code:"
                value={v.diagnosticCode || ''}
                onChange={(e) => onChange?.({ ...v, diagnosticCode: e.target.value })}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
              />
            </Box>

            {/* Referral and Reason */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Referral"
                    value={v.referral || ''}
                    onChange={(e) => onChange?.({ ...v, referral: e.target.value })}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Reason"
                    value={v.reason || ''}
                    onChange={(e) => onChange?.({ ...v, reason: e.target.value })}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* TPAs */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">TPAs</Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={printTPAs}
                  disabled={!v.tpas}
                >
                  Print
                </Button>
              </Box>
              <TextField
                value={v.tpas || ''}
                onChange={(e) => onChange?.({ ...v, tpas: e.target.value })}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder="Enter TPA information"
              />
            </Box>

            {/* Recall Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Recall</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Recall"
                    type="number"
                    value={v.recallValue || ''}
                    onChange={(e) => handleRecallChange('recallValue', e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={v.recallFrequency || ''}
                      onChange={(e) => handleRecallChange('recallFrequency', e.target.value)}
                      label="Frequency"
                    >
                      <MenuItem value="day">Day</MenuItem>
                      <MenuItem value="week">Week</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Recall Date"
                    type="date"
                    value={v.recallDate || ''}
                    onChange={(e) => onChange?.({ ...v, recallDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DiagnosisTab;